import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { functions }    from '@/db/firestore.ts';
import { Guid } from 'guid-typescript';

import { db, decksCollection, dueCardNotifications, workerTasks, Timestamp } from '@/db/firestore';
import { DeckModel } from '@/models/deck';
import { CardModel } from '@/models/card';
import { UserAnswers } from '@/models/UserAnswers';
import { DueCardNotificationModel } from '@/models/DueCardNotification';

import { Mapper } from '@/db/Mapper';

export class Decks {

  public empty(): DeckModel {
    return {
        id: Guid.toString(),
        name: '',
        cards: [ { guid: "", question: '', answer: '', createdAt: '' }],
        createdBy: '',
        createdAt: Timestamp.now(),
        roles: {}
    };
  }

  public add(deck: DeckModel) {
    return decksCollection.add(deck);
  }

  public async addAsync(deck: DeckModel) {
    const mapper = new Mapper(DeckModel);
    const ref = await decksCollection.add(deck);
    const snapshot = await ref.get();
    const data = mapper.AutoMap(snapshot);
    return data;
  }

  public getById(deckId: string) {
    return decksCollection.doc(deckId);
  }

  public async getByIdAsync(deckId: string): Promise<DeckModel> {
    const mapper = new Mapper(DeckModel);
    const snapshot = await decksCollection.doc(deckId).get();
    const deck = mapper.AutoMap(snapshot);
    return deck;
  }

  public getByIdwithUA(deckId: string, userId: string) {
    return decksCollection.doc(deckId).collection('userAnswers').where('userId', '==', userId).get();
  }

  public async updateDeck(deck: DeckModel, deckId: string) {
    deck.lastupdatedAt = Timestamp.now();
    const ref =  decksCollection.doc(deckId);
    await ref.update(deck);
  }

  public async deleteDeck(deckId: string, userId: string) {
    functions.httpsCallable('deleteUserAnswers')({ deckId, userId }).catch(e => console.error(e));
    functions.httpsCallable('deleteAllDeckStats')({ deckId, userId }).catch(e => console.error(e));

    const deckRef = await decksCollection.doc(deckId);

    return db.runTransaction((transaction) => {
      return transaction.get(deckRef).then((val) => {
        if (!val.exists) throw new Error('Document does not exist!');

        // tslint:disable prefer-const
        let newRoles = val.data().roles;
        delete newRoles[userId];
        transaction.update(deckRef, { roles: newRoles });
      });
    }).then(() => {
      functions.httpsCallable('updateUserUsage')();
    }).catch((error) => console.log("Transaction failed: ", error));
    
  }

  public getUserDecks(uid: string) {
    return decksCollection.where(`roles.${uid}`, '==', 'owner');
  }

  public async getUserDecksAsync(uid: string): Promise<DeckModel[]> {
    let mapper = new Mapper(DeckModel);
    let deckSnapshot = await decksCollection.where(`roles.${uid}`, '==', 'owner').get();
    let decks = mapper.AutoMapList(deckSnapshot);
    return decks;
  }

  public async getUserSharedDecksAsync(uid: string): Promise<DeckModel[]> {
    let mapper = new Mapper(DeckModel);
    let deckSnapshot = await decksCollection.where(`roles.${uid}`, '==', 'reader').get();
    let decks = mapper.AutoMapList(deckSnapshot);
    return decks;
  }

  public getUserSharedDecks(uid: string) {
    return decksCollection.where(`roles.${uid}`, '==', 'reader');
  }

  public getPublicDecks() {
    return decksCollection.where('visibility', '==', 'public');
  }

  public async getUserAnswersAsync(deckId: string, userId: string): Promise<UserAnswers[]> {
    let mapper = new Mapper(UserAnswers);
    let snapshot = await decksCollection.doc(deckId).collection('userAnswers').where('userId', '==', userId).get();
    let userAnswer = mapper.AutoMapList(snapshot);    
    return userAnswer;
  }

  public async deleteUserAnswers(deckId: string, userId: string) {
    const promise = new Promise((resolve, reject) => {
      decksCollection.doc(deckId).collection('userAnswers')
      .get()
      .then(docs => {
        docs.forEach((doc) => doc.ref.delete());
      })
      .then(() => resolve())
      .catch((e) => reject('there was an error deleting:' + e));
    });
    return promise;
  }

  public async deleteUserAnswer(deckId: string, userId: string, cardGuid: string) {
    functions.httpsCallable('deleteDueCards')({ deckId, userId, cardGuid }).catch(e => console.error(e));

    const promise = new Promise((resolve, reject) => {
      decksCollection.doc(deckId).collection('userAnswers')
      .where('cardGuid', '==', cardGuid)
      .where('userId', '==', userId)
      .get()
      .then(docs => {
        docs.forEach((doc) => doc.ref.delete());
      })
      .then(() => resolve())
      .catch((e) => reject('there was an error deleting:' + e));
    });
    return promise;
  }

  public async getDeckAsync(deckId: string): Promise<firebase.firestore.DocumentSnapshot> {
    return decksCollection.doc(deckId).get();
  }

  public async getUserAnswerAsync(deckId: string, userId: string, cardId: string): Promise<UserAnswers> {
    let mapper = new Mapper(UserAnswers);
    let snapshot = await decksCollection.doc(deckId).collection('userAnswers').where('userId', '==', userId).where('cardGuid', '==', cardId).get();
    let userAnswer = mapper.AutoMap(snapshot);    
    return userAnswer;
  }

  public async getUserAnswersByDeckAsync(deckId: string, userId: string) {
    const myAnswerQuery = decksCollection.doc(deckId).collection('userAnswers').where('userId', '==', userId);
    const myAnswers = await myAnswerQuery.get().then((snapshot) => { const answers = snapshot.docs.map((answer) => answer.data()); return answers; })
    return myAnswers;
  }

  public async getAllUserAnswersAsync(userId: string) {
    let mapper = new Mapper(UserAnswers);

    const answersQuery = db.collectionGroup('userAnswers')
      .where('userId', '==', userId);

    const snapshot = await answersQuery.get();
    let answers = mapper.AutoMapList(snapshot);
    return answers;

  }

  public async getDueUserAnswersAsync(userId: string): Promise<UserAnswers[]> {
    let mapper = new Mapper(UserAnswers);
    let currentTime = Timestamp.now();

    const answersQuery = db.collectionGroup('userAnswers')
      .where('userId', '==', userId)
      .where('nextIntervalDate', '>', currentTime);

    const answerSnapshot = await answersQuery.get();
    let answers = mapper.AutoMapList(answerSnapshot);
    return answers;

  }

  public async getDueCards(deckId: string, userId: string) {
    const doc = await this.getDeckAsync(deckId);
    const thisDeck = doc.data();
    const myAnswers = await this.getUserAnswersByDeckAsync(deckId, userId);

    const deckCards: CardModel[] = thisDeck.cards;
    // tslint:disable prefer-const
    let dueCards: CardModel[] = [];

    deckCards.forEach((thisCard) => {
      const thisAnswer = myAnswers === null ? null : myAnswers.find((ua) => ua.cardGuid === thisCard.guid);
      // if no answer ever, then it needs to be reviewed asap
      if (!(thisAnswer)) {
        dueCards.push(thisCard);
      }
      // otherwise, only review the answer if it is time
      else {
        const cardDue = thisAnswer.nextIntervalDate.toDate();
        let currentDate = new Date();
        if (cardDue < currentDate) {
          dueCards.push(thisCard);
        }
      }
    });
    thisDeck.cards = dueCards;
    return thisDeck;
  }

  // tslint:disable member-ordering
  public userAnswers = {
    getById: (deckId: string, uaId: string) => {
      return decksCollection.doc(deckId).collection('userAnswers').doc(uaId);
    },
    addAsync: async (userAnswer: UserAnswers): Promise<UserAnswers> => {
      let mapper = new Mapper(UserAnswers);
      // why do we use the spread operator to cast here, do you ask? 
      // good question. firebase wont' take a custom type - it needs to be cast as a plain old json object
      let ref = await decksCollection.doc(userAnswer.DeckId).collection('userAnswers').add({...userAnswer});
      let snapshot = await ref.get();
      let data = mapper.AutoMap(snapshot);
      return data;
    },
    updateAsync: async (userAnswer: UserAnswers): Promise<void> => {
      let ref = decksCollection.doc(userAnswer.DeckId).collection('userAnswers').doc(userAnswer.id);
      await ref.update({...userAnswer});
    },
    getLatest: async (deckId: string, userId: string): Promise<UserAnswers> => {
      let mapper = new Mapper(UserAnswers);
      let snapshot = await decksCollection.doc(deckId).collection('userAnswers').where('userId', '==', userId).orderBy('lastAnswered', 'desc').limit(1).get();
      let data = mapper.AutoMap(snapshot); 
      return data;
    },
    createNotifications: (parentId: string, userId: string, cardGuid: string, uaId: string, uswerAnswer: UserAnswers) => {
      let batch = db.batch();
      let notifyRef;
      
      if (!uaId) {
        console.error("No UA ID in createNotifications!")
        notifyRef = dueCardNotifications.doc();
      } else {
        notifyRef = dueCardNotifications.doc(uaId);
      }

      const thisNotify: DueCardNotificationModel = {
        deckId: parentId,
        userId,
        cardGuid,
        due: uswerAnswer.nextIntervalDate,
        queued: false,
      };

      batch.set(notifyRef, thisNotify);

      const liscioSworkerTask = workerTasks.doc(`liscioSlack-${uaId}`);
      const thisTask = {
        worker: 'liscioSlack',
        status: 'scheduled',
        performAt: uswerAnswer.nextIntervalDate,
        userId,
        deckId: parentId,
        options: thisNotify,
      };

      batch.set(liscioSworkerTask, thisTask);

      const notifyTime = workerTasks.doc(`notifyTime-${uaId}`);
      thisTask.worker = 'notifyTime';

      batch.set(notifyTime, thisTask);

      return batch.commit();
    },
  };

}
